import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { PackageStatusBadge } from "@/components/StatusBadge";
import { Layers, FilePlus2, Search } from "lucide-react";
import { useWarehousePermissions } from "@/hooks/useWarehousePermissions";

export default function PackageSorting() {
  const navigate = useNavigate();
  const { can } = useWarehousePermissions();
  const canAdd = can("add_to_batch");
  const canCreate = can("create_batch");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [batchDlg, setBatchDlg] = useState(false);
  const [chosenBatch, setChosenBatch] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [{ data: pkgs }, { data: bts }] = await Promise.all([
        api.get(`/warehouse/packages`, { params: { status: "RECEIVED_AT_WAREHOUSE,SORTING_IN_WAREHOUSE", search: search || undefined } }),
        api.get(`/warehouse/batches`, { params: { status: "DRAFT,OPEN_FOR_ADDING_PACKAGES" } }),
      ]);
      setRows(pkgs);
      setBatches(bts);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const toggle = (pid) => {
    const s = new Set(selected);
    s.has(pid) ? s.delete(pid) : s.add(pid);
    setSelected(s);
  };

  const selectedIds = Array.from(selected);

  const addToExisting = async () => {
    if (!chosenBatch || selectedIds.length === 0) return;
    setBusy(true);
    try {
      await api.post(`/warehouse/batches/${chosenBatch}/add-packages`, { package_ids: selectedIds });
      toast.success(`Added ${selectedIds.length} package(s) to batch.`);
      setBatchDlg(false);
      setSelected(new Set());
      setChosenBatch("");
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const createNewBatch = () => {
    if (selectedIds.length === 0) return;
    navigate(`/warehouse/batches/new?packages=${selectedIds.join(",")}`);
  };

  return (
    <div data-testid="wh-sorting-page" className="space-y-5">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-1">/ Sorting</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Package Sorting</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">Pick packages to group into an outbound batch.</p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); fetchAll(); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="wh-sorting-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by QR, package, store, receiver…"
            className="pl-9 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white"
          />
        </div>
        <Button type="submit" className="bg-cyan-400 text-slate-950 hover:bg-cyan-300">Search</Button>
      </form>

      <div className="sticky top-0 z-10 bg-white/95 dark:bg-slate-950/95 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          <span className="font-mono-tech text-cyan-300">{selectedIds.length}</span>
          <span className="text-slate-600 dark:text-slate-400"> selected</span>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="wh-sort-add-existing"
            disabled={!canAdd || selectedIds.length === 0 || batches.length === 0}
            onClick={() => setBatchDlg(true)}
            className="bg-slate-100 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 disabled:opacity-40"
          >
            <Layers className="w-4 h-4 mr-2" /> Add to Existing Batch
          </Button>
          <Button
            data-testid="wh-sort-create-new"
            disabled={!canCreate || selectedIds.length === 0}
            onClick={createNewBatch}
            className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 disabled:opacity-40"
          >
            <FilePlus2 className="w-4 h-4 mr-2" /> Create New Batch
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-14 text-center text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-14 text-center bg-slate-100/30 dark:bg-slate-900/30">
          <Layers className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="font-display text-base text-slate-700 dark:text-slate-300 mb-1">Nothing to sort</div>
          <div className="text-xs text-slate-500">Confirm incoming packages first.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map(pkg => (
            <label
              key={pkg.id}
              data-testid={`wh-sort-row-${pkg.package_id}`}
              className={`border rounded-lg p-3 flex items-center gap-3 cursor-pointer ${selected.has(pkg.package_id) ? "border-cyan-500/50 bg-cyan-500/5" : "border-slate-200 dark:border-slate-800 bg-slate-100/40 dark:bg-slate-900/40"}`}
            >
              <Checkbox
                data-testid={`wh-sort-check-${pkg.package_id}`}
                checked={selected.has(pkg.package_id)}
                onCheckedChange={() => toggle(pkg.package_id)}
                className="border-slate-300 dark:border-slate-600"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono-tech text-xs text-cyan-300 truncate">{pkg.qr_code_id}</div>
                  <PackageStatusBadge status={pkg.status} />
                </div>
                <div className="font-display font-semibold text-slate-900 dark:text-white truncate">{pkg.package_name}</div>
                <div className="text-xs text-slate-600 dark:text-slate-400 truncate">{pkg.store_name} → {pkg.receiver_name} · {pkg.receiver_address}</div>
              </div>
            </label>
          ))}
        </div>
      )}

      <Dialog open={batchDlg} onOpenChange={setBatchDlg}>
        <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
          <DialogHeader>
            <DialogTitle>Add to Existing Batch</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto scrollbar-thin">
            {batches.length === 0 && <div className="text-slate-500 text-sm">No open batches.</div>}
            {batches.map(b => (
              <label
                key={b.id}
                data-testid={`wh-sort-batch-${b.id}`}
                className={`border rounded-md p-3 flex items-center gap-3 cursor-pointer ${chosenBatch === b.id ? "border-cyan-500/50 bg-cyan-500/5" : "border-slate-200 dark:border-slate-800"}`}
              >
                <input
                  type="radio"
                  name="batch"
                  checked={chosenBatch === b.id}
                  onChange={() => setChosenBatch(b.id)}
                  className="accent-cyan-400"
                />
                <div className="flex-1">
                  <div className="font-mono-tech text-xs text-cyan-300">{b.batch_code}</div>
                  <div className="font-display font-semibold">{b.batch_name}</div>
                  <div className="text-xs text-slate-500">
                    {b.total_packages || 0} pkg · {b.destination_area || "—"}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchDlg(false)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white">Cancel</Button>
            <Button
              data-testid="wh-sort-confirm-add"
              disabled={!chosenBatch || busy}
              onClick={addToExisting}
              className="bg-cyan-400 text-slate-950 hover:bg-cyan-300"
            >
              Add {selectedIds.length}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
